import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { User, IdCard, KeyRound, Bell, ShieldCheck, AlertTriangle, CheckCircle2, MailWarning } from 'lucide-react';
import { Field, Tabs, Toggle, AuthImage, Spinner, FileInput, fmtDate, dateInput } from '../../components/ui';
import api, { errMsg } from '../../services/api';
import { useGroup, useMember } from '../../context/AuthContext';

const YEAR = 365 * 24 * 60 * 60 * 1000;

function Banner({ tone, icon: Icon, children }) {
  const tones = { amber: 'border-amber-200 bg-amber-50 text-amber-900', gold: 'border-gold-200 bg-gold-50 text-navy-900', red: 'border-red-200 bg-red-50 text-red-800' };
  return <div className={`mb-4 flex flex-wrap items-center gap-3 rounded-xl border p-4 text-sm ${tones[tone]}`}><Icon size={20} className="flex-shrink-0" /><div className="flex flex-1 flex-wrap items-center justify-between gap-3">{children}</div></div>;
}

function DetailsTab({ member, setMember, classes }) {
  const [form, setForm] = useState({
    fullName: member.fullName || '', nameWithInitials: member.nameWithInitials || '', className: member.className || '',
    phone: member.phone || '', city: member.city || '', country: member.country || '', profession: member.profession || '',
    dateOfBirth: dateInput(member.dateOfBirth),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (member.hasSriLankanNic) delete payload.dateOfBirth;
      const { data } = await api.put('/member/me', payload);
      setMember(data);
      toast.success('Details saved');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
      <Field label="Full name" required className="sm:col-span-2"><input className="input" required value={form.fullName} onChange={e => set('fullName', e.target.value)} /></Field>
      <Field label="Name with initials"><input className="input" value={form.nameWithInitials} onChange={e => set('nameWithInitials', e.target.value)} /></Field>
      <Field label="Class">
        {classes.length ? (
          <select className="input" value={form.className} onChange={e => set('className', e.target.value)}>
            <option value="">—</option>
            {[...new Set([...classes, form.className].filter(Boolean))].map(c => <option key={c}>{c}</option>)}
          </select>
        ) : <input className="input" value={form.className} onChange={e => set('className', e.target.value)} />}
      </Field>
      <Field label="NIC number" hint="Contact an admin if this is wrong"><input className="input" disabled value={member.nicNumber || 'No Sri Lankan NIC'} /></Field>
      <Field label="Date of birth" hint={member.hasSriLankanNic ? 'Read from your NIC' : undefined}>
        <input type="date" className="input" disabled={member.hasSriLankanNic} value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} />
      </Field>
      <Field label="Mobile number" required><input type="tel" className="input" required value={form.phone} onChange={e => set('phone', e.target.value)} /></Field>
      <Field label="Profession"><input className="input" value={form.profession} onChange={e => set('profession', e.target.value)} /></Field>
      <Field label="City"><input className="input" value={form.city} onChange={e => set('city', e.target.value)} /></Field>
      <Field label="Country"><input className="input" value={form.country} onChange={e => set('country', e.target.value)} /></Field>
      <div className="sm:col-span-2"><button disabled={busy} className="btn-primary">{busy && <Spinner size={16} />}Save details</button></div>
    </form>
  );
}

function RcuTab({ member, setMember }) {
  const [form, setForm] = useState({ rcuCardNumber: member.rcuCardNumber || '', rcuRegisteredDate: dateInput(member.rcuRegisteredDate) });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imgKey, setImgKey] = useState(0);

  const save = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('rcuCardNumber', form.rcuCardNumber);
      fd.append('rcuRegisteredDate', form.rcuRegisteredDate);
      if (file) fd.append('rcuCardImage', file);
      const { data } = await api.put('/member/me/rcu', fd);
      setMember(data);
      setFile(null);
      setImgKey(k => k + 1);
      toast.success('RCU membership details updated');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="grid gap-5 md:grid-cols-[1fr_260px]">
      <div className="space-y-4">
        <p className="flex items-center gap-2 text-sm">
          Status: {member.rcuVerified
            ? <span className="badge bg-green-100 text-green-800"><CheckCircle2 size={12} /> Confirmed by admin</span>
            : <span className="badge bg-amber-100 text-amber-800">Awaiting admin check</span>}
        </p>
        <Field label="RCU card number"><input className="input" required value={form.rcuCardNumber} onChange={e => setForm(f => ({ ...f, rcuCardNumber: e.target.value }))} /></Field>
        <Field label="Registered date"><input type="date" className="input" required value={form.rcuRegisteredDate} onChange={e => setForm(f => ({ ...f, rcuRegisteredDate: e.target.value }))} /></Field>
        <Field label="Replace card photo" hint="JPG, PNG or PDF up to 8 MB"><FileInput accept="image/*,.pdf" file={file} onChange={setFile} /></Field>
        <p className="text-xs text-gray-500">Changing these details sends them to an admin to check again.</p>
        <button disabled={busy} className="btn-primary">{busy && <Spinner size={16} />}Save</button>
      </div>
      <div>
        <p className="label">Current card</p>
        {member.hasRcuCard ? <AuthImage key={imgKey} src="/member/me/rcu-card" alt="RCU card" className="w-full rounded-lg border object-contain" /> : <p className="text-sm text-gray-400">No card uploaded</p>}
      </div>
    </form>
  );
}

function AccountTab({ member, setMember }) {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [busy, setBusy] = useState('');

  const changeEmail = async e => {
    e.preventDefault();
    setBusy('email');
    try {
      const { data } = await api.post('/member/me/email', { email });
      setMember(data);
      setEmail('');
      toast.success(`Verification sent to ${data.pendingEmail}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy('');
    }
  };

  const cancelChange = async () => {
    const { data } = await api.delete('/member/me/email');
    setMember(data);
  };

  const changePassword = async e => {
    e.preventDefault();
    if (pw.newPassword !== pw.confirm) return toast.error('Passwords do not match');
    setBusy('pw');
    try {
      await api.put('/member/me/password', pw);
      setPw({ currentPassword: '', newPassword: '', confirm: '' });
      toast.success('Password changed');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="grid gap-8 md:grid-cols-2">
      <form onSubmit={changeEmail} className="space-y-4">
        <h3 className="font-semibold text-navy-900">Email address</h3>
        <p className="text-sm text-gray-600">Current: <strong>{member.email}</strong> {member.emailVerifiedAt && <CheckCircle2 size={14} className="inline text-green-600" />}</p>
        {member.pendingEmail && (
          <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
            Waiting for you to verify <strong>{member.pendingEmail}</strong>. <Link to="/verify-email" className="underline">Enter code</Link> · <button type="button" onClick={cancelChange} className="underline">Cancel change</button>
          </div>
        )}
        <Field label="New email address" hint="We'll send a verification code to the new address. Your current email keeps working until it is verified.">
          <input type="email" className="input" required value={email} onChange={e => setEmail(e.target.value)} />
        </Field>
        <button disabled={busy === 'email'} className="btn-primary">{busy === 'email' && <Spinner size={16} />}Change email</button>
      </form>
      <form onSubmit={changePassword} className="space-y-4">
        <h3 className="font-semibold text-navy-900">Password</h3>
        <Field label="Current password"><input type="password" className="input" required value={pw.currentPassword} onChange={e => setPw(p => ({ ...p, currentPassword: e.target.value }))} autoComplete="current-password" /></Field>
        <Field label="New password" hint="At least 8 characters"><input type="password" className="input" required minLength={8} value={pw.newPassword} onChange={e => setPw(p => ({ ...p, newPassword: e.target.value }))} autoComplete="new-password" /></Field>
        <Field label="Confirm new password"><input type="password" className="input" required value={pw.confirm} onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} autoComplete="new-password" /></Field>
        <button disabled={busy === 'pw'} className="btn-primary">{busy === 'pw' && <Spinner size={16} />}Change password</button>
      </form>
    </div>
  );
}

const PREFS = [
  ['prefAgm', 'AGM news', 'Committee appointments and AGM announcements'],
  ['prefEvents', 'Events', 'Batch get-togethers and event invitations'],
  ['prefProjects', 'School projects', 'Projects at the College and calls for support'],
  ['prefGeneral', 'General announcements', 'Other news from the committee'],
  ['prefDocuments', 'New documents', 'When an important document is added to the library'],
  ['prefBirthday', 'Birthday wishes', 'A greeting from the batch on your birthday'],
];

function NotificationsTab({ member, setMember }) {
  const update = async patch => {
    try {
      const { data } = await api.put('/member/me/preferences', patch);
      setMember(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="space-y-5">
      {member.unsubscribed && (
        <Banner tone="amber" icon={MailWarning}>
          <span>You have unsubscribed from all announcements.</span>
          <button onClick={() => update({ unsubscribed: false })} className="btn-gold btn-sm">Turn emails back on</button>
        </Banner>
      )}
      <p className="text-sm text-gray-600">Choose which emails you'd like to receive. Account emails (verification, password resets) are always sent.</p>
      <div className="space-y-4">
        {PREFS.map(([k, label, desc]) => (
          <Toggle key={k} checked={member[k] && !member.unsubscribed} disabled={member.unsubscribed} onChange={v => update({ [k]: v })} label={label} description={desc} />
        ))}
      </div>
      {!member.unsubscribed && (
        <button onClick={() => update({ unsubscribed: true })} className="text-sm text-red-600 underline">Unsubscribe from all announcements</button>
      )}
    </div>
  );
}

function PrivacyTab({ member, setMember }) {
  const [busy, setBusy] = useState(false);
  const request = async cancel => {
    if (!cancel && !window.confirm('Ask the committee to permanently delete your member record and all your details?')) return;
    setBusy(true);
    try {
      const { data } = await api.post('/member/me/deletion-request', { cancel });
      setMember(data);
      toast.success(cancel ? 'Deletion request withdrawn' : 'Deletion request sent to the committee');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4 text-sm text-gray-700">
      <p>Your details are used only for group communication and are never shared or sold. Only appointed admins can see your contact details, NIC number and RCU card.</p>
      <p>You gave consent on <strong>{fmtDate(member.consentAt)}</strong>. <Link to="/privacy" className="text-navy-700 underline">Read the privacy statement</Link>.</p>
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <h3 className="font-semibold text-red-800">Delete my data</h3>
        {member.deletionRequestedAt ? (
          <>
            <p className="mt-1 text-red-800">You asked for your data to be deleted on {fmtDate(member.deletionRequestedAt)}. An admin will process it shortly.</p>
            <button disabled={busy} onClick={() => request(true)} className="btn-outline btn-sm mt-3">Withdraw request</button>
          </>
        ) : (
          <>
            <p className="mt-1 text-red-800">Ask the committee to permanently remove your member record. You will stop receiving all emails and lose access to Group documents.</p>
            <button disabled={busy} onClick={() => request(false)} className="btn-danger btn-sm mt-3">Request deletion</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Profile() {
  const { member, setMember } = useMember();
  const { group } = useGroup();
  const [tab, setTab] = useState('details');

  useEffect(() => { window.scrollTo(0, 0); }, [tab]);

  const confirmNow = async () => {
    const { data } = await api.post('/member/me/confirm-details');
    setMember(data);
    toast.success('Thanks for confirming!');
  };

  const lastConfirmed = member.detailsConfirmedAt ? new Date(member.detailsConfirmedAt).getTime() : 0;
  const dueForConfirm = member.status === 'ACTIVE' && Date.now() - lastConfirmed > YEAR - 30 * 24 * 60 * 60 * 1000;

  const tabs = [
    { id: 'details', label: 'My details', icon: User },
    { id: 'rcu', label: 'RCU membership', icon: IdCard },
    { id: 'account', label: 'Email & password', icon: KeyRound },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Privacy', icon: ShieldCheck },
  ];

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-navy-900 font-display text-2xl font-bold text-gold-400">
          {member.fullName.charAt(0)}
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold text-navy-900">{member.fullName}</h1>
          <p className="text-sm text-gray-500">Class {member.className || '—'} · Member since {fmtDate(member.createdAt)}</p>
        </div>
      </div>

      {member.status === 'PENDING' && (
        <Banner tone="amber" icon={AlertTriangle}>
          <span>Please verify your email address to become an active member and access Group documents.</span>
          <Link to="/verify-email" className="btn-gold btn-sm">Verify now</Link>
        </Banner>
      )}
      {dueForConfirm && (
        <Banner tone="gold" icon={CheckCircle2}>
          <span>Are your details still correct? Please check them and confirm once a year.</span>
          <button onClick={confirmNow} className="btn-primary btn-sm">Yes, all correct</button>
        </Banner>
      )}

      <div className="card p-5 md:p-6">
        <Tabs tabs={tabs} active={tab} onChange={setTab} />
        {tab === 'details' && <DetailsTab member={member} setMember={setMember} classes={group?.classes || []} />}
        {tab === 'rcu' && <RcuTab member={member} setMember={setMember} />}
        {tab === 'account' && <AccountTab member={member} setMember={setMember} />}
        {tab === 'notifications' && <NotificationsTab member={member} setMember={setMember} />}
        {tab === 'privacy' && <PrivacyTab member={member} setMember={setMember} />}
      </div>
    </div>
  );
}
